/**
 * Secondary G3 stream page content: semantics + FAQ + decision-support data
 * plus the thin builder that runs the shared generator and attaches them.
 */
import { buildStreamPageContent } from './secondaryStreamGenerator';
import type { DecisionProofPoint, FaqItem, LevelPageContent } from './types';

const G3_SEMANTICS: Record<string, any> = {
  keySuffix: 'G3',
  streamName: 'G3 stream',
  seoLabel: 'G3',
  eyebrowLabel: 'G3 MATHEMATICS',
  lower: {
    seoDescription:
      'The MOE G3 Mathematics syllabus for Lower Secondary, topic by topic — plus weekly live classes that build algebraic fluency and non-routine problem solving.',
    headline: 'Develop advanced reasoning with strong conceptual depth.',
    subheadFocus:
      'algebraic fluency, logical reasoning, and confidence with non-routine problems.',
    syllabusBody:
      'Students are challenged with deeper extensions while ensuring prerequisite fundamentals remain secure.',
    syllabusTopics: [
      {
        category: 'Advanced Algebra Foundations',
        items: [
          'Algebraic expressions, equations, and identities',
          'Function reasoning and graph interpretation',
          'Multi-step algebraic problem solving',
        ],
      },
      {
        category: 'Geometry & Higher-Order Reasoning',
        items: [
          'Geometric relationships and formal reasoning',
          'Coordinate geometry with strategic setup',
          'Mensuration with layered application',
        ],
      },
      {
        category: 'Data, Probability & Non-Routine Tasks',
        items: [
          'Data interpretation under varied conditions',
          'Probability with structured reasoning',
          'Challenge problems combining multiple concepts',
        ],
      },
    ],
    syllabusNote:
      'Lower Secondary G3 should balance rigor with clarity, so students can scale into Upper Secondary without conceptual gaps.',
    challengeFocusTitle: 'Adapting to higher cognitive demand',
    challengeFocusDescription:
      'Questions become more abstract and less procedural, requiring stronger reasoning and method selection.',
    streamSupportTitle: 'Concept-first teaching with strategic challenge',
    streamSupportDescription:
      'We emphasise why methods work, then build fluency through targeted, progressively harder practice.',
    progressionGoal:
      'enter exam years with both conceptual confidence and technical fluency.',
  },
  upper: {
    seoDescription:
      'The MOE G3 Mathematics syllabus for Upper Secondary, topic by topic across E-Math and A-Math — plus weekly live classes and O-Level exam preparation.',
    headline: 'Excel in O-Level E-Math and A-Math with strategic rigor.',
    subheadFocus:
      'integrating algebra, trigonometry, and higher-order techniques for exam performance.',
    syllabusBody:
      'Preparation emphasises precise working, topic integration, and exam execution across E-Math and A-Math demands.',
    syllabusTopics: [
      {
        category: 'O-Level E-Math Mastery',
        items: [
          'Comprehensive algebra and geometry application',
          'Probability, statistics, and interpretation accuracy',
          'Multi-topic exam problem solving',
        ],
      },
      {
        category: 'A-Math Core Competencies',
        items: [
          'Advanced algebraic manipulation and identities',
          'Trigonometric equations and transformations',
          'Calculus foundations and applications',
        ],
      },
      {
        category: 'Exam Technique & Precision',
        items: [
          'Question triage and mark-maximising strategy',
          'Working discipline to reduce avoidable errors',
          'Timed paper routines for stable performance',
        ],
      },
    ],
    syllabusNote:
      'Upper Secondary G3 success comes from rigorous concept mastery plus disciplined exam execution.',
    challengeFocusTitle: 'Handling complex mixed-topic papers',
    challengeFocusDescription:
      'Students may know individual topics but still struggle when papers integrate many concepts under time pressure.',
    streamSupportTitle: 'Rigorous practice loops and error analysis',
    streamSupportDescription:
      'DeepThink uses targeted diagnostics, correction workflows, and strategic teaching to lift consistency in exam conditions.',
    progressionGoal:
      'approach O-Level papers with precision, confidence, and exam-ready stamina.',
  },
};

const G3_FAQS: Record<string, FaqItem[]> = {
  '1-g3': [
    {
      question: 'What topics does Sec 1 G3 Math cover?',
      answer:
        'Sec 1 G3 covers primes and HCF/LCM, integers and real numbers, algebraic manipulation, linear equations and functions, geometry (angles, polygons), percentages, ratios, and introductory statistics — at an accelerated pace.',
    },
    {
      question:
        'My child scored well in PSLE — do they still need tuition in Sec 1 G3?',
      answer:
        'PSLE success does not guarantee a smooth Sec 1 transition. The jump to algebraic reasoning, formal proofs, and faster pacing catches many high-scoring students off guard. Early support prevents confidence dips.',
    },
    {
      question: 'How demanding is G3 compared to the old Express stream?',
      answer:
        'G3 is broadly equivalent to the former Express stream in content and pace. Students are expected to handle abstract reasoning and multi-step problems from the start of Sec 1.',
    },
    {
      question: 'Will Sec 1 G3 Math prepare my child for A-Math later?',
      answer:
        'Yes. The algebraic fluency and reasoning skills built in Sec 1 G3 are direct prerequisites for Additional Mathematics in Upper Secondary. Stronger Sec 1 foundations make A-Math significantly more manageable.',
    },
    {
      question: 'What if my child finds Sec 1 G3 too fast-paced?',
      answer:
        'If your child is struggling with the pace, targeted support can help them keep up without switching streams. We identify specific gaps and reinforce them so students stay on track without feeling overwhelmed.',
    },
    {
      question: 'Will my Sec 1 G3 child sit O-Levels or the new SEC exam?',
      answer:
        'Current Sec 1 students will sit the Singapore-Cambridge SEC, which replaces all three national secondary exams (O-Level, N(A)-Level, N(T)-Level) from 2027. G3 Math aligns to the former O-Level standard. Students receive one unified certificate and can mix G-levels across subjects. The content and skills remain the same — strong G3 foundations are still the best preparation.',
    },
  ],
  '2-g3': [
    {
      question: 'What does Sec 2 G3 Math cover?',
      answer:
        'Sec 2 G3 covers advanced algebraic manipulation, quadratic equations, simultaneous equations, coordinate geometry, trigonometry, geometric proofs, probability, and statistical analysis — at a pace that prepares students for both E-Math and A-Math.',
    },
    {
      question: 'Should my Sec 2 G3 child start thinking about A-Math?',
      answer:
        'Yes. Sec 2 G3 builds the algebraic fluency that A-Math demands. Students considering A-Math should ensure their algebra, equation-solving, and graph interpretation are secure by the end of Sec 2.',
    },
    {
      question: 'How important are Sec 2 G3 foundations for exam performance?',
      answer:
        'Very important. Nearly every Sec 3-4 topic extends Sec 2 concepts. Students who enter Upper Secondary with strong Sec 2 foundations spend less time relearning and more time mastering exam-level application.',
    },
    {
      question: 'Is Sec 2 G3 harder than Sec 1 G3?',
      answer:
        'The content is more demanding — topics like quadratic equations, trigonometry, and geometric proofs require higher-level reasoning. Students who were comfortable in Sec 1 sometimes find the Sec 2 jump surprising.',
    },
    {
      question:
        'My child is in G3 but seems to be struggling — should they move to G2?',
      answer:
        'Not necessarily. Many Sec 2 G3 students hit a temporary difficulty wall when topics become more abstract. Targeted support to close specific gaps often resolves the issue without requiring a stream change.',
    },
    {
      question: 'How does the new SEC exam affect my Sec 2 G3 child?',
      answer:
        'Current Sec 2 G3 students will sit the Singapore-Cambridge SEC in Sec 4 — a single national exam replacing all three former leaving exams. G3 Math aligns to the former O-Level standard. The content and skills required remain the same — strong Sec 2 G3 foundations are the best preparation.',
    },
  ],
  '3-g3': [
    {
      question: 'What does Sec 3 G3 Math cover?',
      answer:
        'Sec 3 G3 covers advanced algebra (including polynomials and partial fractions), trigonometric identities and equations, coordinate geometry, logarithms and indices, and introduction to calculus for A-Math students.',
    },
    {
      question: 'Should my Sec 3 G3 child take both E-Math and A-Math?',
      answer:
        'Most G3 students take both. E-Math covers the core syllabus while A-Math adds calculus, trigonometry, and advanced algebra. Taking both broadens JC and polytechnic options significantly.',
    },
    {
      question: 'How do E-Math and A-Math differ in Sec 3?',
      answer:
        'E-Math focuses on applied problem-solving across a broad syllabus. A-Math introduces more abstract concepts — differentiation, trigonometric proofs, and algebraic manipulation — that require deeper reasoning and fluency.',
    },
    {
      question: 'Is Sec 3 the hardest year for G3 Math?',
      answer:
        'Many students find Sec 3 the most challenging because the volume of new, demanding content is at its peak. Sec 4 focuses more on revision and exam technique, which feels more manageable once the concepts are understood.',
    },
    {
      question:
        "My child's A-Math is much weaker than E-Math — what should we do?",
      answer:
        'This is common. A-Math requires different skills — algebraic manipulation, proof techniques, and abstract reasoning. Targeted A-Math support in Sec 3 is critical because the Sec 4 revision window is too short to learn from scratch.',
    },
    {
      question: 'Will my Sec 3 G3 child sit the SEC exam or O-Levels?',
      answer:
        'Current Sec 3 students are the first cohort sitting the Singapore-Cambridge SEC in 2027 — a single national exam replacing all three former leaving exams (O-Level, N(A)-Level, N(T)-Level). G3 Math aligns to the former O-Level standard. E-Math and A-Math subject options continue under the SEC framework, and students can mix G-levels across subjects.',
    },
  ],
  '4-g3': [
    {
      question: 'What exams does a Sec 4 G3 / Express student sit?',
      answer:
        'G3 (Express) students sit the O-Level examinations — typically both Elementary Mathematics (E-Math) and Additional Mathematics (A-Math). Strong performance in both opens the widest range of JC and polytechnic options.',
    },
    {
      question:
        'How should my Express / G3 child balance E-Math and A-Math revision?',
      answer:
        'Most students find E-Math more manageable, so they should allocate proportionally more revision time to A-Math. However, E-Math should not be neglected — careless errors on the "easier" paper can cost unexpected marks.',
    },
    {
      question:
        'When should Sec 4 G3 / Express students start doing full exam papers?',
      answer:
        'Ideally by the start of Term 3, once the full syllabus is covered. Before that, focus on topic mastery and timed topical practice. Full papers are most useful when the student can attempt every question.',
    },
    {
      question:
        'What are the most common reasons G3 / Express students underperform in O-Levels?',
      answer:
        'Careless errors under time pressure, incomplete revision of the full syllabus (skipping weaker topics), and poor exam technique — such as not showing sufficient working for method marks.',
    },
    {
      question:
        'My child is scoring well in E-Math but poorly in A-Math — what should we prioritise?',
      answer:
        'A-Math improvement. A weak A-Math grade limits JC stream options and suggests gaps in algebraic reasoning that will matter for H2 Math. Target the weakest A-Math topics first — usually calculus or trigonometric proofs.',
    },
  ],
};

const G3_DECISION: Record<string, DecisionProofPoint[]> = {
  '1-g3': [
    {
      title: 'Your child scored well in PSLE but is struggling in Sec 1',
      description:
        'The G3 pace and abstraction level is a significant jump from primary school. Many high-PSLE-scoring students need support adjusting to formal algebra and proof-style reasoning.',
    },
    {
      title:
        'Your child understands in class but cannot solve problems independently',
      description:
        'This gap between "following along" and "doing it alone" is common in Sec 1 G3. It indicates the student needs more structured practice to internalise methods, not just exposure.',
    },
    {
      title: 'You are concerned about A-Math readiness later',
      description:
        'Sec 1 G3 algebra and reasoning skills are direct prerequisites for A-Math. Investing in strong foundations now significantly reduces the difficulty of A-Math when it begins in Sec 3.',
    },
  ],
  '2-g3': [
    {
      title: 'Your child is considering A-Math for Upper Secondary',
      description:
        'A-Math requires strong algebraic fluency that must be built in Lower Secondary. If your child is planning to take A-Math, Sec 2 is the year to ensure these foundations are rock-solid.',
    },
    {
      title: 'Sec 2 topics feel harder than Sec 1',
      description:
        'The Sec 2 G3 jump — quadratic equations, trigonometry, geometric proofs — is real and catches many students by surprise. Early support prevents a mid-year slump.',
    },
    {
      title: 'Your child understands concepts but makes careless errors',
      description:
        'Persistent "careless" errors usually indicate incomplete procedural fluency, not inattention. Structured practice that builds automaticity with core skills resolves this pattern.',
    },
  ],
  '3-g3': [
    {
      title:
        'Your child has started A-Math and is finding it very different from E-Math',
      description:
        'A-Math requires a different mode of thinking — abstract manipulation, formal proofs, and unfamiliar techniques. Many G3 students need separate support for A-Math even if E-Math is manageable.',
    },
    {
      title: 'The volume of new Sec 3 content is overwhelming',
      description:
        'Sec 3 G3 introduces more new topics than any other year. Falling behind on even one chapter creates a cascading effect because later topics build on earlier ones.',
    },
    {
      title: 'Your child wants to enter JC and needs strong O-Level results',
      description:
        'JC admission depends heavily on O-Level grades. Building strong topic mastery in Sec 3 frees up Sec 4 for exam practice and fine-tuning rather than learning from scratch.',
    },
  ],
  '4-g3': [
    {
      title:
        'Your child needs to improve both E-Math and A-Math grades for JC admission',
      description:
        'JC cut-off points consider aggregate scores. Improving weaker subjects has a larger impact on the aggregate than marginal gains in stronger ones. We help students prioritise strategically.',
    },
    {
      title: 'A-Math topics like calculus or trigonometry are still not secure',
      description:
        'If key A-Math topics remain shaky in Sec 4, targeted intervention is urgent. These topics carry significant marks and cannot be avoided in the exam — attempting them unprepared costs more marks than skipping them.',
    },
    {
      title:
        'Your child can do questions in practice but underperforms in exams',
      description:
        'This performance gap signals a need for exam-condition training — timed papers, question triage, and working discipline. Regular exam simulation builds the stamina and strategy to perform reliably.',
    },
  ],
};

export function buildSecondaryG3Content(
  secLevel: number,
  brandName = 'DeepThink'
): LevelPageContent {
  const content = buildStreamPageContent(
    secLevel,
    G3_SEMANTICS,
    'g3',
    brandName
  );
  const dataKey = `${secLevel}-g3`;
  return {
    ...content,
    ...(G3_FAQS[dataKey] && { faqItems: G3_FAQS[dataKey] }),
    ...(G3_DECISION[dataKey] && {
      decisionProofPoints: G3_DECISION[dataKey],
    }),
  };
}
